import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Receipt, DollarSign, CheckCircle2, Clock, Flower2, Download, TrendingUp } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startLogin } from "@/components/Navbar";

const StatusBadge = ({ status }) => {
  const paid = status === "COMPLETED";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${paid ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
      {paid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
      {paid ? "Paid" : (status || "Pending")}
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const monthlySummary = (orders) => {
  const map = {};
  orders.filter((o) => o.status === "COMPLETED").forEach((o) => {
    const d = new Date(o.captured_at || o.created_at);
    if (isNaN(d)) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString(undefined, { month: "long", year: "numeric" });
    if (!map[key]) map[key] = { key, label, count: 0, revenue: 0 };
    map[key].count += 1;
    map[key].revenue += Number(o.amount || 0);
  });
  return Object.values(map).sort((a, b) => b.key.localeCompare(a.key));
};

const exportCsv = (orders) => {
  const headers = ["Order ID", "Design", "Customer", "Email", "Amount (USD)", "Status", "Created", "Paid"];
  const rows = orders.map((o) => [
    o.order_id, o.bouquet_name || "", o.buyer_name || "", o.buyer_email || "",
    Number(o.amount || 0).toFixed(2), o.status || "", o.created_at || "", o.captured_at || "",
  ].map(csvCell).join(","));
  const csv = [headers.map(csvCell).join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `quote-requests-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export default function Orders() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { setData(null); return; }
    api.get("/admin/orders")
      .then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 403) setForbidden(true); setData(null); });
  }, [user, loading]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Receipt className="w-7 h-7 text-primary" />
          <h1 className="font-serif-display text-4xl sm:text-5xl">Quote Requests</h1>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <p className="text-muted-foreground">Every paid request customers have sent your way.</p>
          {data && data.orders.length > 0 && (
            <button
              onClick={() => exportCsv(data.orders)}
              data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border border-primary text-primary hover:bg-primary/5 transition-all"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          )}
        </div>

        {!user && !loading ? (
          <div className="text-center py-24">
            <Flower2 className="w-10 h-10 text-primary mx-auto mb-4" />
            <p className="text-lg mb-6">Sign in with your owner account to view quote requests.</p>
            <button onClick={startLogin} data-testid="orders-signin-btn" className="rounded-full px-7 py-3 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all">Sign in</button>
          </div>
        ) : forbidden ? (
          <div className="text-center py-24">
            <p className="text-lg text-muted-foreground mb-6">This page is only available to the store owner.</p>
            <button onClick={() => navigate("/studio")} className="rounded-full px-7 py-3 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all">Back to studio</button>
          </div>
        ) : data === undefined ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
              <StatCard icon={CheckCircle2} label="Paid requests" value={data.paid_count} />
              <StatCard icon={DollarSign} label="Total revenue" value={`$${data.revenue.toFixed(2)}`} />
              <StatCard icon={Receipt} label="All records" value={data.orders.length} />
            </div>

            {(() => {
              const months = monthlySummary(data.orders);
              const max = Math.max(1, ...months.map((m) => m.revenue));
              return months.length > 0 ? (
                <div className="bg-white rounded-2xl border border-border p-6 mb-8" data-testid="monthly-summary">
                  <div className="flex items-center gap-2 mb-5">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    <h2 className="font-serif-display text-2xl">Monthly revenue</h2>
                  </div>
                  <div className="space-y-4">
                    {months.map((m) => (
                      <div key={m.key} data-testid={`month-row-${m.key}`}>
                        <div className="flex justify-between text-sm mb-1.5">
                          <span className="font-medium">{m.label}</span>
                          <span className="text-muted-foreground">
                            {m.count} paid · <span className="text-primary font-semibold">${m.revenue.toFixed(2)}</span>
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(m.revenue / max) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {data.orders.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">No quote requests yet.</div>
            ) : (
              <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="orders-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="px-5 py-4 font-semibold">Design</th>
                        <th className="px-5 py-4 font-semibold">Customer</th>
                        <th className="px-5 py-4 font-semibold">Email</th>
                        <th className="px-5 py-4 font-semibold">Amount</th>
                        <th className="px-5 py-4 font-semibold">Status</th>
                        <th className="px-5 py-4 font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.orders.map((o, i) => (
                        <motion.tr
                          key={o.order_id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.03 }}
                          className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors"
                          data-testid={`order-row-${o.order_id}`}
                        >
                          <td className="px-5 py-4 font-medium">{o.bouquet_name || "—"}</td>
                          <td className="px-5 py-4">{o.buyer_name || "—"}</td>
                          <td className="px-5 py-4 text-muted-foreground">{o.buyer_email || "—"}</td>
                          <td className="px-5 py-4 font-medium">${Number(o.amount || 0).toFixed(2)}</td>
                          <td className="px-5 py-4"><StatusBadge status={o.status} /></td>
                          <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">{fmtDate(o.captured_at || o.created_at)}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-border p-5">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="font-serif-display text-3xl text-primary">{value}</p>
    </div>
  );
}
